import React from 'react';
import { Offer } from 'types';
import {
  Text,
  Box,
  Heading,
  Price,
  Image,
} from 'components';
import { ImageTypes } from 'components/Image/Image';
import { Usps } from 'components/Payment';
import { getImageUrl } from 'utils/helpers';
import { useSelector } from 'reducers';
import { getTrialPeriodInDays } from '../CheckoutView/utils';

type Props = {
  offer: Offer,
  onClick?: () => void,
  disabled?: boolean,
  selected?: boolean,
  bestValue?: boolean,
  disableTrial?: boolean,
  onKeyboardSelect?: () => void,
};

export default function OfferComponent(
  { offer, disabled, bestValue, selected, disableTrial, onKeyboardSelect, ...otherProps }: Props,
) {
  const { onClick } = otherProps;

  const {
    location,
  } = useSelector(state => ({ location: state.router.location } ));

  const price = offer.currency ? (
    <Price key="price" price={offer.priceInCents} currency={offer.currency} />
  ) : '';

  const imgUrl = getImageUrl(offer.images, false) || getImageUrl(offer.images, true);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && onClick && !disabled) {
      e.preventDefault();
      onClick();
      if (onKeyboardSelect) {
        onKeyboardSelect();
      }
    }
  };

  return (
    <Box
      position="relative"
      column
      width="20rem"
      xl-width="26rem"
      bg="paperBackground"
      borderRadius="paper"
      shadow={selected ? 'selectedOffer' : 'none'}
      mb="large"
      xs-mt="0"
      p="large"
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      aria-disabled={disabled}
      aria-pressed={selected}
      {...otherProps}
      className="e2e-offer"
      alignItems="stretch"
      maxWidth="100%"
      cursor="pointer"
      {...(disabled ? {
        mt: '-2rem',
        bg: 'currentOfferBackground',
        cursor: 'default',
        overflow: 'hidden',
      } : null)}
    >
      {bestValue && (
        <Box
          position="absolute"
          top="0"
          left="50%"
          transform="translate(-50%, -50%)"
          bg="brand"
          px="small"
          lineHeight="1.6"
          borderRadius="paper"
        >
          <Text color="primaryButtonTextColor" whiteSpace="nowrap" bold id="payment.bestValue" />
        </Box>
      )}
      {disabled && (
        <Box
          mt="-1.5rem"
          mx="-1.5rem"
          px="large"
          mb="large"
          width="100%"
          color="white"
          bg="whiteButtonBackground"
          boxSizing="content-box"
          textAlign="center"
          lineHeight="2rem"
        >
          <Text fontSize="fine" bold id="payment.currentSubscription" />
        </Box>
      )}
      {imgUrl && (
        <Box mb="large">
          <Image
            alt={offer.title}
            bgColor="paperBackground"
            rounded="6px"
            src={imgUrl}
            type={ImageTypes.offerBanner}
            keepAspectRatio
            fullWidth
          />
        </Box>
      )}
      <Heading
        xl-fontSize="sectionHeading"
        fontSize="large"
        align="center"
        fontWeight="bold"
        wordWrap="break-word"
        as={location?.name === 'offers-change' ? 'h2' : 'h3'}
      >
        {offer.title}
      </Heading>

      <Usps
        type="bullet"
        size="small"
        mt="large"
        usps={offer.usps}
        alignItems="start"
      />

      <Box flex="1" hideEmpty={false} />

      <Box mb="xlarge" xl-mt="xlarge" mt="medium">
        <Text
          id="payment.price"
          values={{
            period: offer.recurringPeriod?.unit,
            price,
          }}
          xl-fontSize="sectionHeading"
          fontSize="large"
          align="center"
          fontWeight="600"
          color="primary"
          as="div"
        />
      </Box>
      {offer.trialPeriod && !disableTrial && (
        <Box mt="-1.5rem" mb="0.4em">
          <Text
            id="payment.trial"
            values={{ days: getTrialPeriodInDays(offer.trialPeriod as any) }}
            bold
            align="center"
            as="div"
          />
        </Box>
      )}
    </Box>
  );
}
